// src/pages/UserDashboard.js
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
} from "firebase/firestore";
import { auth, db } from "../services/firebase";
import API_BASE from "../api/apiBase";

/* ---------------- helpers ---------------- */

function formatMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(2)}`;
}

function formatDate(ts) {
  try {
    if (ts?.toDate) return ts.toDate().toLocaleString();
    return new Date(ts).toLocaleString();
  } catch {
    return "—";
  }
}

/* ---------------- component ---------------- */

export default function UserDashboard() {
  const navigate = useNavigate();

  const [user, setUser] = useState(auth.currentUser);
  const [authReady, setAuthReady] = useState(false);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ NEW: track sharing state per project
  const [sharingIds, setSharingIds] = useState({});

  const ensureAuthReady = async () => {
    const u = auth.currentUser;
    if (!u) throw new Error("Not authenticated");
    await u.getIdToken(true);
    return u;
  };

  /* -------- auth -------- */

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      setAuthReady(true);
      if (u) await u.getIdToken(true);
    });
    return () => unsub();
  }, []);

  /* -------- load projects -------- */

  useEffect(() => {
    const load = async () => {
      if (!authReady || !user?.uid) return;

      setLoading(true);

      const q = query(
        collection(db, "projects"),
        where("ownerUid", "==", user.uid),
        orderBy("createdAt", "desc")
      );

      const snap = await getDocs(q);
      setProjects(snap.docs.map((d) => ({ id: d.id, ...d.data() })));

      setLoading(false);
    };

    load();
  }, [authReady, user]);

  const isShared = (p) => p?.sharedWithContractor === true;

  /* -------- SHARE -------- */

  const handleShareWithContractor = async (projectId) => {
    try {
      // ✅ set loading state for this project
      setSharingIds((prev) => ({ ...prev, [projectId]: true }));

      const u = await ensureAuthReady();

      console.log("API_BASE:", API_BASE);

      const base =
        API_BASE && API_BASE.length > 0
          ? API_BASE
          : "https://api-staging.measuresmart.ai";

      const url = `${base}/api/share-project`;
      console.log("Calling:", url);

      const token = await u.getIdToken();

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ projectId }),
      });

      const text = await res.text();
      console.log("share-project response:", text);

      if (!res.ok) {
        throw new Error("Share request failed");
      }

      // ✅ update UI AFTER backend success
      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId
            ? { ...p, sharedWithContractor: true }
            : p
        )
      );

    } catch (err) {
      console.error("Share failed:", err);
      alert("Failed to share project. Please try again.");
    } finally {
      // ✅ clear loading state
      setSharingIds((prev) => ({ ...prev, [projectId]: false }));
    }
  };

  /* ---------------- UI ---------------- */

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: 20 }}>

      <h1 style={{ color: "#0b3a6a", fontWeight: 900 }}>
        Your Projects
      </h1>

      <p style={{ color: "#4b5563", fontWeight: 600 }}>
        Review your saved measurement projects.
      </p>

      <button
        onClick={() => navigate("/measure")}
        style={{
          background: "#1e5cc8",
          color: "#fff",
          padding: "14px 22px",
          borderRadius: 14,
          border: "none",
          fontWeight: 900,
          marginTop: 10,
          marginBottom: 20,
        }}
      >
        New Measurement
      </button>

      {projects.map((p) => (
        <div
          key={p.id}
          style={{
            background: "#f3f7ff",
            borderRadius: 18,
            padding: 22,
            marginBottom: 18,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
          }}
        >
          {/* LEFT */}
          <div>
            <div style={{ fontWeight: 900, fontSize: 20 }}>
              {p.projectName} {isShared(p) && "• Shared"}
            </div>

            <div style={{ fontWeight: 700 }}>
              {formatDate(p.createdAt)}
            </div>

            <div style={{ marginTop: 10 }}>
              <div>Rooms: {p.summary?.roomsCount || 0}</div>
              <div>
                Total AI Area: {p.summary?.totalAiArea?.toFixed(2)} sq ft
              </div>
              <div>
                Total AI Cost: {formatMoney(p.summary?.totalAiCost)}
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div style={{ display: "flex", gap: 12 }}>
            <Link
              to={`/dashboard/project/${p.id}`}
              style={{
                background: "#0f172a",
                color: "#fff",
                padding: "14px 18px",
                borderRadius: 14,
                fontWeight: 900,
                textDecoration: "none",
              }}
            >
              View Details
            </Link>

            <button
              disabled={isShared(p) || sharingIds[p.id]}
              onClick={() => handleShareWithContractor(p.id)}
              style={{
                background: isShared(p) ? "#9ca3af" : "#374151",
                color: "#fff",
                padding: "14px 18px",
                borderRadius: 14,
                fontWeight: 900,
                border: "none",
              }}
            >
              {isShared(p)
                ? "Shared"
                : sharingIds[p.id]
                ? "Sharing..."
                : "Share"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
